import React, { useContext } from 'react'
import { Form, StepHeading, StepDescription } from 'components/GetStarted/styles'
import TextInput, { LABEL_POSITION } from 'components/generic/Input/TextInput'
import PrimaryButton from 'components/generic/PrimaryButton'
import { trackClickEvent } from 'utils/Analytics'
import { useForm } from 'react-hook-form'
import SelectInput from 'components/generic/Input/SelectInput'
import { InputContainer, ErrorMessage, DollarSign, SkipText } from './styles'
import { getHeadingForUserType } from 'utils/getStartedUtils'
import { USER_TYPES } from 'components/GetStarted/constants'
import { AppContext } from 'contexts/AppContext'

export const RATE_TYPES = {
  DAILY: 'daily_rate',
  HOURLY: 'hourly_rate',
}

const StepEnterRate = ({ data, userInput, onStepComplete }) => {
  const { domain } = useContext(AppContext)
  const { register, handleSubmit, errors, formState, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      hourly_rate: userInput?.hourly_rate,
      rate_type: userInput?.rate_type,
    },
  })
  const isSelectWageTypeEnabled = data?.features?.wage_type_select
  const minimumWage = (userInput?.location?.state?.minimumWage ?? 0) / 100
  const stateName = userInput?.location?.state?.name
  const { isValid } = formState
  const heading = getHeadingForUserType({
    userType: userInput?.user_type,
    providerHeading: 'What rate do you charge?',
    seekerHeading: 'What salary are you offering?',
  })
  const isProvider = userInput?.user_type === USER_TYPES.PROVIDER
  const canSkipStep = isProvider && getValues('hourly_rate')?.length === 0
  const rateValidationRules = {
    required: 'Please enter a rate.',
    validate: (value) => value >= minimumWage || `Enter at least the min. wage ($${minimumWage}).`,
  }
  const onNextClick = () => {
    trackClickEvent(domain, 'get-started-next-rate')
    if (canSkipStep) {
      onStepComplete({
        rate_type: null,
        hourly_rate: null,
      })
    }
  }
  return (
    <Form onSubmit={handleSubmit(onStepComplete)}>
      <StepHeading>{heading}</StepHeading>
      <StepDescription>
        Must be at least ${minimumWage}/hr ({stateName}’s minimum wage)
      </StepDescription>
      <InputContainer>
        <DollarSign isSelectWageTypeEnabled={isSelectWageTypeEnabled}>$</DollarSign>
        <TextInput
          id='hourly-rate'
          autoFocus={true}
          type='number'
          name='hourly_rate'
          step='0.01'
          min='0'
          label='Per Hour'
          isValid={!errors.hourly_rate}
          labelPosition={isSelectWageTypeEnabled ? LABEL_POSITION.ABOVE : LABEL_POSITION.RIGHT}
          ref={register(rateValidationRules)}
        />
        {isSelectWageTypeEnabled && (
          <SelectInput
            name='rate_type'
            label='Rate Type'
            options={[
              { id: RATE_TYPES.HOURLY, name: 'Per Hour' },
              { id: RATE_TYPES.DAILY, name: 'Per Day' },
            ]}
            emptyOptionLabel={false}
            ref={register()}
          />
        )}
      </InputContainer>
      <ErrorMessage errors={errors} inputId='hourly-rate' name='hourly_rate' />
      {canSkipStep && <SkipText>Want to set your rate later?</SkipText>}
      <PrimaryButton
        mt='2rem'
        disabled={!isValid && !canSkipStep}
        type={canSkipStep ? 'button' : undefined}
        onClick={onNextClick}
      >
        {canSkipStep ? 'Skip' : 'Next'}
      </PrimaryButton>
    </Form>
  )
}

export default StepEnterRate
